import * as React from 'react'
import * as API from '../lib/api'
import { PaymentDetails } from '../lib/payment_details'
import { TaxInformation } from '../lib/tax_information'
import { TrialExplainer } from './TrialExplainer'

// "What Happens Next" — the three-node timeline that replaces the "About Your
// Plan" bullets. Pure presentational: every date/price is derived from props the
// checkout already carries, mirroring the control `TermsOfService` date math.
export interface ITimelineProps {
  trial: boolean
  trialLength: number
  totalIncludedItemsInTrial: number
  planName: string
  // Per-product-type trial download breakdown (CD-172), ordered server-side by
  // `SiteProductType#display_order`.
  includedItemsInTrial: API.ITrialDownloadItem[]
  paymentDetails: PaymentDetails
  taxInformation: TaxInformation
  loyaltyRenewalDiscount: number
  // Injection point for deterministic date math in tests; defaults to now.
  today?: Date
}

interface ITimelineNode {
  id: string
  dateLabel: string
  heading: string
  amount?: string
  // The trial's first node embeds the Trial Explainer trigger inline, so this is
  // a ReactNode rather than a string.
  description: React.ReactNode
}

// Matches the control checkout's currency rendering (`Cart#formatPrice`).
function formatCurrency(amount: number): string {
  return `$${amount.toFixed(2)}`
}

// Matches the control's date rendering (`TermsOfService#endOfTrial`). The design
// uppercases it; that's left to CSS.
function formatDate(date: Date): string {
  return date.toLocaleString('en-us', { month: 'long', day: 'numeric', year: 'numeric' })
}

function addDays(date: Date, days: number): Date {
  const result = new Date(date)
  result.setDate(result.getDate() + days)
  return result
}

// Day 0 of the next month is the last day of this one.
function daysInMonth(year: number, monthIndex: number): number {
  return new Date(year, monthIndex + 1, 0).getDate()
}

// `setMonth`/`setFullYear` roll over when the source day doesn't exist in the
// target month (Jan 31 + 1 month -> Mar 3). Clamp to the last valid day so
// billing/renewal dates never drift.
function addMonths(date: Date, months: number): Date {
  const result = new Date(date)
  const day = result.getDate()
  result.setDate(1)
  result.setMonth(result.getMonth() + months)
  result.setDate(Math.min(day, daysInMonth(result.getFullYear(), result.getMonth())))
  return result
}

function addYears(date: Date, years: number): Date {
  const result = new Date(date)
  const day = result.getDate()
  const month = result.getMonth()
  result.setDate(1)
  result.setFullYear(result.getFullYear() + years)
  result.setDate(Math.min(day, daysInMonth(result.getFullYear(), month)))
  return result
}

// Read off the count rather than the control's `installments.length` so we don't
// depend on the moment-backed installment objects.
function isMonthly(paymentDetails: PaymentDetails): boolean {
  return paymentDetails.numberOfPayments > 1
}

function buildNodes(props: ITimelineProps): ITimelineNode[] {
  const {
    trial,
    trialLength,
    totalIncludedItemsInTrial,
    planName,
    includedItemsInTrial,
    paymentDetails,
    taxInformation,
    loyaltyRenewalDiscount,
  } = props

  const today = props.today || new Date()
  const monthly = isMonthly(paymentDetails)
  const total = paymentDetails.total({ taxInformation })

  // Recurring monthly billing starts at trial end for trials; a paid checkout
  // takes its first installment today, so recurring charges begin a month later.
  const billingBeginsDate = trial ? addDays(today, trialLength) : addMonths(today, 1)
  // Membership term start: trial end for trials, today for a paid checkout.
  const membershipStartDate = trial ? addDays(today, trialLength) : today
  const renewalDate = addYears(membershipStartDate, 1)

  return [firstNode(), secondNode(), thirdNode()]

  function firstNode(): ITimelineNode {
    // The trial turns "{N} free downloads" into the Trial Explainer trigger
    // (CD-172); the paid flow has no download count.
    const description: React.ReactNode = trial ? (
      <>
        Your {trialLength}-day free trial begins as soon as you complete this checkout. You&apos;ll
        have{' '}
        <TrialExplainer planName={planName} items={includedItemsInTrial}>
          {totalIncludedItemsInTrial} free downloads
        </TrialExplainer>{' '}
        to enjoy with your trial, and can cancel at any time.
      </>
    ) : (
      'Your membership begins as soon as you complete this checkout.'
    )

    return {
      id: '1',
      dateLabel: 'Today',
      heading: 'Total Due',
      amount: formatCurrency(trial ? 0 : total),
      description,
    }
  }

  function secondNode(): ITimelineNode {
    if (monthly) {
      // `total` is already the per-installment, tax-inclusive monthly amount (the
      // backend sets `price_before_tax = installments.first.amount`), so render it
      // directly — do not divide by numberOfPayments again.
      const description = trial
        ? `Unless you cancel your trial, it will roll into a paid membership after ` +
          `${trialLength} days (or earlier, if you activate it), and your card will be ` +
          `automatically charged every month for ${paymentDetails.numberOfPayments} months.`
        : `Your card will be automatically charged ${formatCurrency(total)} every month ` +
          `for ${paymentDetails.numberOfPayments} months.`

      return {
        id: '2',
        dateLabel: formatDate(billingBeginsDate),
        heading: 'Monthly Installments Begin',
        description,
      }
    }

    // Annual plans take a single payment, so node 2 anchors on membership start,
    // not the recurring-monthly date. For a paid annual plan the card was already
    // charged in node 1, so the copy speaks to the active term.
    const description = trial
      ? `Unless you cancel your trial, it will roll into a paid membership after ` +
        `${trialLength} days (or earlier, if you activate it), and your card will be ` +
        `charged ${formatCurrency(total)} for the year.`
      : `Your membership is active for the full year.`

    return {
      id: '2',
      dateLabel: formatDate(membershipStartDate),
      heading: 'Membership Begins',
      description,
    }
  }

  function thirdNode(): ITimelineNode {
    return {
      id: '3',
      dateLabel: formatDate(renewalDate),
      heading: 'Automatic Renewal',
      description:
        `Your membership will automatically renew with a loyalty discount of ` +
        `${formatCurrency(loyaltyRenewalDiscount)} after the first year, unless you turn ` +
        `automatic renewal off.`,
    }
  }
}

export function Timeline(props: ITimelineProps) {
  const nodes = buildNodes(props)

  return (
    <section className="trial-timeline" data-testid="trial-timeline" aria-label="What happens next">
      <ol className="trial-timeline__nodes" role="list">
        {nodes.map((node, index) => {
          const isLast = index === nodes.length - 1

          return (
            <li
              key={node.id}
              className="trial-timeline__node"
              data-testid={`trial-timeline-node-${node.id}`}
            >
              <span aria-hidden="true" className="trial-timeline__marker" />
              {!isLast && <span aria-hidden="true" className="trial-timeline__connector" />}
              <p className="trial-timeline__date">{node.dateLabel}</p>
              <h3 className="trial-timeline__heading">
                {node.heading}
                {node.amount ? (
                  <span className="trial-timeline__amount"> {node.amount}</span>
                ) : null}
              </h3>
              <p className="trial-timeline__description">{node.description}</p>
            </li>
          )
        })}
      </ol>
    </section>
  )
}
